'use client';

import type { Swiper as SwiperCore } from 'swiper';
import type { VerticalProductTabItemProps } from '@weberus/ui/components';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { type FC } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { VerticalProductTabItem } from '@weberus/ui/components';
import { useAtomValue } from 'jotai';
import { clientLogStandardized, YaTrackerImpression } from '@weberus/client-tracker';
import { waitUntilClickTimeout } from '@weberus/utils';
import { pcWidthAtom } from '@/app/_shared/store/device';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { SwiperNavButton } from '../../common';

export interface ProductAreaProps {
  pageNameLog: HomeLogPageNameValue;
  widgetLogMeta: Record<string, unknown>;
  tabItems: VerticalProductTabItemProps[][];
  tabLogMeta: Record<string, unknown>;
  setSelectedIndex: (index: number) => void;
  swiperRef: React.MutableRefObject<SwiperCore | null>;
}

export const ProductArea: FC<ProductAreaProps> = ({
  pageNameLog,
  widgetLogMeta,
  tabItems,
  tabLogMeta,
  setSelectedIndex,
  swiperRef,
}) => {
  const { isPcWidth } = useAtomValue(pcWidthAtom);

  const showNavButton = tabItems.length > 1;

  const onClickHandler = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    tabIndex: number,
    item: VerticalProductTabItemProps,
  ) => {
    e.preventDefault();
    await waitUntilClickTimeout(
      clientLogStandardized('click', pageNameLog, {
        widgetPagePosition: tabIndex,
        ...widgetLogMeta,
        ...tabLogMeta,
        ...item.logMeta,
      }),
    );
    item.action && navigateTo(item.action);
  };

  return (
    <div className="relative pc:w-pc-size">
      <Swiper
        loop={isPcWidth}
        spaceBetween={isPcWidth ? 20 : 0}
        onRealIndexChange={(e) => {
          setSelectedIndex(e.realIndex);
        }}
        onSwiper={(swiper: SwiperCore) => {
          swiperRef.current = swiper;
        }}>
        {tabItems.map((items, tabIndex) => (
          <SwiperSlide key={`${tabIndex}_${isPcWidth}`}>
            <div className="flex items-center px-16 pc:px-0">
              <div className="flex w-full flex-col space-y-12 pc:flex-row pc:gap-20 pc:space-y-0">
                {items.map((item, index) => (
                  <YaTrackerImpression
                    key={index}
                    onImpressed={() =>
                      clientLogStandardized('impression', pageNameLog, {
                        widgetPagePosition: tabIndex,
                        ...widgetLogMeta,
                        ...tabLogMeta,
                        ...item.logMeta,
                      })
                    }>
                    <VerticalProductTabItem {...item} onClick={(e) => onClickHandler(e, tabIndex, item)} />
                  </YaTrackerImpression>
                ))}
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {!!showNavButton && <SwiperNavButton swiperRef={swiperRef} top={136} />}
    </div>
  );
};
